import varaLogo from '@assets/images/vara-logo.png'
import dldLogo from '@assets/images/dubai_land_dep.png'
import xrpLedgerLogo from '@assets/images/xrp-ledger-logo.png'
import dubaiRestLogo from '@assets/images/dubai-rest-logo.png'
import rentalIncomeIndexIcon from '@assets/images/rental-income-index-icon.svg'
import towerPhoto from '@assets/images/auth-showcase-tower.jpg'
import emiratesTowersPhoto from '@assets/images/auth-showcase-emirates-towers.jpg'
import sailTowerPhoto from '@assets/images/auth-showcase-sail-tower.jpg'
import goldTowerPhoto from '@assets/images/auth-showcase-gold-tower.jpg'
import frameTowerPhoto from '@assets/images/auth-showcase-frame-tower.jpg'

// The five "why sell with Sliceal" slides that rotate in the photo column beside
// every pre-auth screen. Each photo is pre-cropped to the region its Figma frame
// shows, so the panel only needs `object-fit: cover`.

/**
 * Glass badge shape behind the partner mark.
 * - `pill`: hugs a wide wordmark (VARA, DLD, XRP Ledger).
 * - `tile`: a fixed 126×78 square-ish tile for a compact emblem (Dubai REST,
 *   the Rental Income Index icon).
 */
export type AuthShowcaseBadgeShape = 'pill' | 'tile'

export interface AuthShowcaseSlide {
  id: string
  title: string
  description: string
  photo: string
  /** Strength of the dark wash over the photo, 0–1. Brighter photos need more. */
  washOpacity: number
  logo: string
  logoAlt: string
  /** Rendered size of the mark inside its badge, in px. */
  logoWidth: number
  logoHeight: number
  /**
   * How the mark fills that box. Defaults to `contain`; `cover` crops the
   * transparent margin of an export whose canvas is much taller than the mark
   * (the DLD lockup).
   */
  logoFit?: 'contain' | 'cover'
  badgeShape: AuthShowcaseBadgeShape
}

export const AUTH_SHOWCASE_SLIDES: readonly AuthShowcaseSlide[] = [
  {
    id: 'vara',
    title: 'VARA Licensed',
    description:
      'Every tokenized sale runs through VARA-regulated channels, keeping your transaction secure, compliant, and fully above board.',
    photo: towerPhoto,
    washOpacity: 0.66,
    logo: varaLogo,
    logoAlt: 'Virtual Assets Regulatory Authority',
    logoWidth: 54,
    logoHeight: 22,
    badgeShape: 'pill',
  },
  {
    id: 'dld',
    title: 'DLD Regulated',
    description:
      'Your ownership and transfer are recorded with the Dubai Land Department, so your title stays legally protected throughout the sale.',
    photo: emiratesTowersPhoto,
    washOpacity: 0.8,
    logo: dldLogo,
    logoAlt: 'Dubai Land Department',
    logoWidth: 105,
    logoHeight: 19,
    logoFit: 'cover',
    badgeShape: 'pill',
  },
  {
    id: 'xrp-ledger',
    title: 'On the XRP Ledger',
    description:
      'Each share you sell is recorded on-chain — a transparent, tamper-proof record that gives buyers the confidence to buy faster.',
    photo: sailTowerPhoto,
    washOpacity: 0.77,
    logo: xrpLedgerLogo,
    logoAlt: 'XRP Ledger',
    logoWidth: 85,
    logoHeight: 22,
    badgeShape: 'pill',
  },
  {
    id: 'dubai-rest',
    title: 'Dubai REST',
    description:
      'Title, Ejari, and transfer are handled through Dubai REST — no paperwork, no queues, no waiting at counters.',
    photo: goldTowerPhoto,
    washOpacity: 0.77,
    logo: dubaiRestLogo,
    logoAlt: 'Dubai REST',
    logoWidth: 64,
    logoHeight: 50,
    badgeShape: 'tile',
  },
  {
    id: 'rental-income-index',
    title: 'Rental Income Index',
    description:
      'We price your property against current Dubai market data, so you list at a number that’s fair to you and quick to sell.',
    photo: frameTowerPhoto,
    washOpacity: 0.8,
    logo: rentalIncomeIndexIcon,
    logoAlt: 'Rental Income Index',
    logoWidth: 41,
    logoHeight: 38,
    badgeShape: 'tile',
  },
]

/** How long each slide stays up before the panel advances on its own. */
export const AUTH_SHOWCASE_INTERVAL_MS = 4000
